package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import evoshift.composeapp.generated.resources.contract_owner_partner
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.LeaveAllowance
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.model.TeamLeaveBalance
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.leave.GetTeamLeaveBalancesUseCase
import com.evoapps.evoshift.domain.usecase.leave.SaveLeaveAllowanceUseCase
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.ui.reports.FileSaver
import com.evoapps.evoshift.ui.user.LeaveBalancesExport
import com.evoapps.evoshift.ui.user.LeaveExportLabels
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import com.evoapps.evoshift.util.appLanguage
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.contract_b2b
import evoshift.composeapp.generated.resources.contract_civil
import evoshift.composeapp.generated.resources.contract_employment
import evoshift.composeapp.generated.resources.contract_specific_task
import evoshift.composeapp.generated.resources.employee_not_set
import evoshift.composeapp.generated.resources.export_col_contract
import evoshift.composeapp.generated.resources.export_col_employee
import evoshift.composeapp.generated.resources.leave_allowance_saved
import evoshift.composeapp.generated.resources.leave_balances_exported
import evoshift.composeapp.generated.resources.leave_csv_carry_over
import evoshift.composeapp.generated.resources.leave_csv_entitlement
import evoshift.composeapp.generated.resources.leave_csv_on_demand_limit
import evoshift.composeapp.generated.resources.leave_csv_on_demand_used
import evoshift.composeapp.generated.resources.leave_csv_pending
import evoshift.composeapp.generated.resources.leave_csv_remaining
import evoshift.composeapp.generated.resources.leave_csv_total
import evoshift.composeapp.generated.resources.leave_csv_used
import evoshift.composeapp.generated.resources.leave_csv_year
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import org.jetbrains.compose.resources.getString

data class LeaveBalancesUiState(
    val isLoading: Boolean = false,
    val userId: String = "",
    val workplaceId: String? = null,
    val year: Int = 0,
    /** Seeing the balances needs [Gate.LEAVE_BALANCES]; the contract's entitlement also [Gate.PAY_AND_PRIVATE_DATA]. */
    val capabilities: Set<Capability> = emptySet(),
    val rows: List<TeamLeaveBalance> = emptyList(),
    /** A save, an export or a failure to tell about. */
    val message: UiText? = null
) {
    val canEdit: Boolean get() = capabilities.shows(Gate.LEAVE_BALANCES_EDITING)
}

/**
 * SH-113: the team's annual leave balances for managers (Team → Leave balances): per person the
 * entitlement (from the contract for owners and financial managers, else a manager's figure), the
 * carry-over, days taken and left; a manager sets the entitlement and carry-over; CSV export.
 */
class LeaveBalancesViewModel(
    private val getTeamLeaveBalances: GetTeamLeaveBalancesUseCase,
    private val saveLeaveAllowance: SaveLeaveAllowanceUseCase,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val fileSaver: FileSaver,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val today: () -> LocalDate = { AppClock.todayIn(TimeZone.currentSystemDefault()) }
) : ViewModel() {

    private val _uiState = MutableStateFlow(LeaveBalancesUiState(year = today().year))
    val uiState: StateFlow<LeaveBalancesUiState> = _uiState.asStateFlow()
    private val loadJob = JobSlot()

    init {
        viewModelScope.launch {
            sessionManager.currentWorkplaceId.collectLatest { workplaceId ->
                val user = getCurrentUser().first() ?: return@collectLatest
                if (workplaceId == null) return@collectLatest
                val capabilities = user.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId))
                _uiState.update { state ->
                    val switched = state.workplaceId != workplaceId
                    state.copy(userId = user.uid, workplaceId = workplaceId, capabilities = capabilities, rows = if (switched) emptyList() else state.rows)
                }
                load()
            }
        }
    }

    /** Another year (the arrows beside it). */
    fun changeYear(delta: Int) {
        _uiState.update { it.copy(year = it.year + delta, rows = emptyList()) }
        load()
    }

    private fun load() {
        val state = _uiState.value
        val workplaceId = state.workplaceId ?: return
        if (!state.capabilities.shows(Gate.LEAVE_BALANCES)) {
            _uiState.update { it.copy(isLoading = false, rows = emptyList()) }
            return
        }
        val year = state.year
        loadJob.launch(viewModelScope) {
            _uiState.update { it.copy(isLoading = it.rows.isEmpty()) }
            getTeamLeaveBalances(workplaceId, year, withPayTerms = state.capabilities.shows(Gate.PAY_AND_PRIVATE_DATA)).fold(
                onSuccess = { rows -> _uiState.update { if (it.year == year && it.workplaceId == workplaceId) it.copy(isLoading = false, rows = rows) else it } },
                onFailure = { e -> _uiState.update { it.copy(isLoading = false, message = e.toUiText()) } }
            )
        }
    }

    /**
     * Sets [userId]'s entitlement ([entitlementDays] null: from the contract) and carry-over for
     * the year shown. Optimistic: the row changes at once, a failed save puts it back and says why.
     */
    fun saveAllowance(userId: String, entitlementDays: Int?, carryOverDays: Int) {
        val before = _uiState.value
        val workplaceId = before.workplaceId ?: return
        if (!before.canEdit) return
        val row = before.rows.firstOrNull { it.userId == userId } ?: return
        val allowance = LeaveAllowance(userId, workplaceId, before.year, entitlementDays, carryOverDays)
        _uiState.update { state -> state.copy(rows = state.rows.map { if (it.userId == userId) row.with(allowance) else it }) }
        viewModelScope.launch {
            saveLeaveAllowance(allowance, before.userId).fold(
                onSuccess = { _uiState.update { it.copy(message = UiText.Resource(Res.string.leave_allowance_saved)) } },
                onFailure = { e -> _uiState.update { it.copy(rows = before.rows, message = e.toUiText()) } }
            )
        }
    }

    /** The balances shown as a CSV, handed to the platform's [FileSaver]. */
    fun export() {
        val state = _uiState.value
        val workplaceId = state.workplaceId ?: return
        if (!state.capabilities.shows(Gate.LEAVE_BALANCES) || state.rows.isEmpty()) return
        viewModelScope.launch {
            runCatching {
                val file = LeaveBalancesExport.file(workplaceId, state.year, state.rows, exportLabels())
                fileSaver.save(file)
                file.name
            }.fold(
                onSuccess = { name -> _uiState.update { it.copy(message = UiText.Resource(Res.string.leave_balances_exported, listOf(name))) } },
                onFailure = { e -> _uiState.update { it.copy(message = e.toUiText()) } }
            )
        }
    }

    fun messageShown() = _uiState.update { it.copy(message = null) }

    private suspend fun exportLabels(): LeaveExportLabels {
        val contracts = mapOf(
            SystemContractType.STANDARD_EMPLOYMENT to getString(Res.string.contract_employment),
            SystemContractType.NOT_SET to getString(Res.string.employee_not_set),
            SystemContractType.CIVIL_LAW_CONTRACT to getString(Res.string.contract_civil),
            SystemContractType.SPECIFIC_TASK_CONTRACT to getString(Res.string.contract_specific_task),
            SystemContractType.B2B_CONTRACTOR to getString(Res.string.contract_b2b),
            SystemContractType.OWNER_PARTNER to getString(Res.string.contract_owner_partner)
        )
        return LeaveExportLabels(
            language = appLanguage(),
            year = getString(Res.string.leave_csv_year),
            employee = getString(Res.string.export_col_employee),
            contract = getString(Res.string.export_col_contract),
            entitlement = getString(Res.string.leave_csv_entitlement),
            carryOver = getString(Res.string.leave_csv_carry_over),
            total = getString(Res.string.leave_csv_total),
            used = getString(Res.string.leave_csv_used),
            pending = getString(Res.string.leave_csv_pending),
            remaining = getString(Res.string.leave_csv_remaining),
            onDemandUsed = getString(Res.string.leave_csv_on_demand_used),
            onDemandLimit = getString(Res.string.leave_csv_on_demand_limit),
            contractName = { contracts.getValue(it) }
        )
    }
}

/** The row with [allowance] in force: the entitlement and carry-over change, the days taken stay. */
private fun TeamLeaveBalance.with(allowance: LeaveAllowance): TeamLeaveBalance = copy(
    entitlement = entitlement.copy(allowance = allowance),
    balance = balance.copy(
        entitlementDays = allowance.entitlementDays ?: entitlement.statutoryDays ?: 0,
        entitlementSetByManager = allowance.entitlementDays != null,
        carryOverDays = allowance.carryOverDays
    )
)
